import axios, { AxiosInstance, AxiosError } from 'axios'
import type { ApiResponse } from '@/types/helpers'

// Type-safe response wrapper
export type ApiResult<T> = Promise<{ data: T }>

// Create axios instance with direct API URL (Cloudflare will route)
export const apiClient: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'https://api.adicl1.com/api/v1',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

console.log(`[API Client] Initialized with baseURL: ${apiClient.defaults.baseURL}`)

// Request interceptor
apiClient.interceptors.request.use(
  (config) => {
    // Add auth token if available
    const token = localStorage.getItem('auth_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor
apiClient.interceptors.response.use(
  (response) => {
    return response
  },
  async (error: AxiosError) => {
    // Handle unauthorized access
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token')
      window.location.href = '/login'
      return Promise.reject(error)
    }

    // Log errors for debugging
    if (error.response?.status && error.response.status >= 500) {
      console.error('[API Client] Server error:', error.response.status, error.config?.url)
    } else if (!error.response) {
      console.error('[API Client] Network error:', error.code, error.config?.url)
    }

    return Promise.reject(error)
  }
)

// API methods
export const api = {
  // Analytics
  getOverview: () => apiClient.get('/analytics/overview'),
  getMetrics: (params?: any) => apiClient.get('/analytics/metrics', { params }),

  // Messages
  getMessages: (params?: any) => apiClient.get('/messages', { params }),
  getMessage: (id: string) => apiClient.get(`/messages/${id}`),
  submitMessage: (data: any) => apiClient.post('/messages', data),

  // P-adic
  getBalls: (params?: any) => apiClient.get('/padic/balls', { params }),
  calculateDistance: (data: any) => apiClient.post('/padic/distance', data),

  // Reputation
  getLeaderboard: (params?: any) => apiClient.get('/reputation/leaderboard', { params }),
  getReputation: (address: string) => apiClient.get(`/reputation/${address}`),

  // Economics
  getDeposits: (params?: any) => apiClient.get('/economics/deposits', { params }),
  getDeposit: (messageId: string) => apiClient.get(`/economics/deposits/${messageId}`),
  getSupplyMetrics: () => apiClient.get('/economics/supply'),
  getEmissionMetrics: () => apiClient.get('/economics/emissions'),
  getTreasuryInfo: () => apiClient.get('/economics/treasury'),
  getGenesisAllocations: () => apiClient.get('/economics/genesis'),
  getTokenBurns: (params?: any) => apiClient.get('/economics/burns', { params }),
  getLatestSnapshot: () => apiClient.get('/economics/snapshot/latest'),
  getSupplyHistory: (params?: any) => apiClient.get('/economics/history/supply', { params }),
  createTreasuryProposal: (data: any) => apiClient.post('/economics/treasury/propose', data),
  voteOnProposal: (data: any) => apiClient.post('/economics/treasury/vote', data),

  // Wallet
  getTransactions: (params?: any) => apiClient.get('/wallet/transactions', { params }),
  getWalletTransactions: (address: string) => apiClient.get(`/wallet/transactions/${address}`),
  getWalletBalance: (address: string) => apiClient.get(`/wallet/balance/${address}`),
  getWalletBalances: (params?: any) => apiClient.get('/wallet/balances', { params }),
  getWalletStats: () => apiClient.get('/wallet/stats'),
  getBalanceHistory: (address: string, hours?: number) =>
    apiClient.get(`/wallet/balance/${address}/history`, { params: { hours } }),

  // Consensus Metrics (Type-safe examples)
  getConsensusOverview: (): ApiResult<ApiResponse<'/api/v1/consensus/overview'>> =>
    apiClient.get('/consensus/overview'),
  getDiversityStats: (): ApiResult<ApiResponse<'/api/v1/consensus/diversity/current'>> =>
    apiClient.get('/consensus/diversity/current'),
  getDiversityHistory: (hours?: number): ApiResult<ApiResponse<'/api/v1/consensus/diversity/history'>> =>
    apiClient.get('/consensus/diversity/history', { params: { hours } }),
  getEnergyConflicts: (activeOnly?: boolean): ApiResult<ApiResponse<'/api/v1/consensus/energy/conflicts'>> =>
    apiClient.get('/consensus/energy/conflicts', { params: { active_only: activeOnly } }),
  getFinalityDetails: (): ApiResult<ApiResponse<'/api/v1/consensus/finality/detailed'>> =>
    apiClient.get('/consensus/finality/detailed'),
  getAdmissibilityStats: (hours?: number): ApiResult<ApiResponse<'/api/v1/consensus/admissibility/stats'>> =>
    apiClient.get('/consensus/admissibility/stats', { params: { hours } }),
  getSecurityScore: (): ApiResult<ApiResponse<'/api/v1/consensus/security'>> =>
    apiClient.get('/consensus/security'),

  // Update and Version Management
  getNetworkVersions: () => apiClient.get('/updates/network-versions'),
  getUpdateStatus: () => apiClient.get('/updates/status'),
  getPeerUpdateInfo: (peerId: string) => apiClient.get(`/updates/peers/${peerId}`),
  getChangelog: () => apiClient.get('/updates/changelog'),
  getAutoUpdateStatus: () => apiClient.get('/updates/auto-update/status'),

  // Peers
  getPeers: (params?: any) => apiClient.get('/peers', { params }),
  getPeerStats: () => apiClient.get('/peers/stats'),

  // Event Tracking
  getEventGaps: (eventType?: string) =>
    apiClient.get('/events/gaps', { params: { event_type: eventType } }),
  getSequenceTrackerStats: (eventType?: string) =>
    apiClient.get('/events/tracker/stats', { params: { event_type: eventType } }),
  replayEvents: (data: { event_type: string; start_sequence: number; end_sequence: number }) =>
    apiClient.post('/events/replay', data),
  resolveGap: (gapId: number) => apiClient.post(`/events/gaps/${gapId}/resolve`),
  autoResolveGaps: (eventType: string) =>
    apiClient.post(`/events/gaps/auto-resolve/${eventType}`),

  // Dashboard APIs
  getDashboardSnapshot: () => apiClient.get('/dashboard/snapshot'),
  getDashboardConsensus: () => apiClient.get('/dashboard/consensus'),
  getDashboardNetwork: () => apiClient.get('/dashboard/network'),
  getDashboardSwarm: () => apiClient.get('/dashboard/swarm'),
  getDashboardActivity: () => apiClient.get('/dashboard/activity'),

  // Governance APIs
  getProposals: (params?: any) => apiClient.get('/governance/proposals', { params }),
  getProposal: (id: string) => apiClient.get(`/governance/proposals/${id}`),
  getVotes: (params?: any) => apiClient.get('/governance/votes', { params }),
  getParameters: () => apiClient.get('/governance/parameters'),
  getParameterHistory: (name: string) => apiClient.get(`/governance/parameters/${name}/history`),
  getGovernanceReceipts: (params?: any) => apiClient.get('/governance/receipts', { params }),
  getGovernanceStats: () => apiClient.get('/governance/stats'),
  getVoterStats: (address: string) => apiClient.get(`/governance/voter/${address}/stats`),

  // PoUW APIs
  getPouwTasks: (params?: any) => apiClient.get('/pouw/tasks', { params }),
  getPouwTask: (id: string) => apiClient.get(`/pouw/tasks/${id}`),
  getPouwWorkers: (params?: any) => apiClient.get('/pouw/workers', { params }),
  getPouwWorker: (address: string) => apiClient.get(`/pouw/workers/${address}`),
  getPouwResults: (params?: any) => apiClient.get('/pouw/results', { params }),
  getPouwReceipts: (params?: any) => apiClient.get('/pouw/receipts', { params }),
  getPouwStats: () => apiClient.get('/pouw/stats'),
  getPouwLeaderboard: (params?: any) => apiClient.get('/pouw/leaderboard', { params }),

  // VRF APIs
  getVrfProofs: (params?: any) => apiClient.get('/vrf/proofs', { params }),
  getVrfProof: (id: string) => apiClient.get(`/vrf/proofs/${id}`),
  verifyVrfProof: (data: any) => apiClient.post('/vrf/verify', data),
  getVrfStats: () => apiClient.get('/vrf/stats'),
  getVrfPublicKeys: () => apiClient.get('/vrf/keys'),

  // Challenge APIs
  getChallengeWindows: (params?: any) => apiClient.get('/challenges/windows', { params }),
  getChallengeWindow: (id: number) => apiClient.get(`/challenges/windows/${id}`),
  getChallenges: (params?: any) => apiClient.get('/challenges', { params }),
  getChallenge: (id: string) => apiClient.get(`/challenges/${id}`),
  getFraudProofs: (params?: any) => apiClient.get('/challenges/fraud-proofs', { params }),
  getArbitrations: (params?: any) => apiClient.get('/challenges/arbitrations', { params }),
  getArbitration: (id: string) => apiClient.get(`/challenges/arbitrations/${id}`),
  getChallengeStats: () => apiClient.get('/challenges/stats'),
  getChallengerStats: (address: string) => apiClient.get(`/challenges/challenger/${address}/stats`),

  // Escrow APIs
  getEscrowLocks: (params?: any) => apiClient.get('/escrow/locks', { params }),
  getEscrowLock: (id: string) => apiClient.get(`/escrow/locks/${id}`),
  getEscrowTransactions: (params?: any) => apiClient.get('/escrow/transactions', { params }),
  getEscrowStats: () => apiClient.get('/escrow/stats'),
  getEscrowOwner: (address: string) => apiClient.get(`/escrow/owner/${address}`),
  getEscrowOwnerStats: (address: string) => apiClient.get(`/escrow/owner/${address}/stats`),
  getEscrowBeneficiary: (address: string) => apiClient.get(`/escrow/beneficiary/${address}`),

  // Streaming APIs
  getStreamingStatus: () => apiClient.get('/streaming/status'),
  getStreamingHealth: () => apiClient.get('/streaming/health'),
  getStreamingMetrics: () => apiClient.get('/streaming/metrics'),
}